"use client";

import {
  createContext, useContext, useState, useEffect,
  useCallback, ReactNode,
} from "react";
import { upsertRegisteredUser } from "@/lib/registered-users-store";

export type AuthProvider = "google" | "apple" | "email" | "telegram";

export type AuthUser = {
  id:       string;
  name:     string;
  email:    string;
  avatar?:  string;
  provider: AuthProvider;
};

type AuthCtx = {
  user:    AuthUser | null;
  loading: boolean;
  signIn:  (user: AuthUser) => void;
  signOut: () => void;
};

const Ctx = createContext<AuthCtx | null>(null);

const KEY         = "blackdoor_auth_v1";
const SESSION_KEY = "blackdoor_session_v1";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,    setUser]    = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) setUser(JSON.parse(raw));
    } catch {}
    setLoading(false);
  }, []);

  const signIn = useCallback((u: AuthUser) => {
    setUser(u);
    localStorage.setItem(KEY, JSON.stringify(u));

    upsertRegisteredUser({
      id:       u.id,
      name:     u.name,
      email:    u.email,
      avatar:   u.avatar,
      provider: u.provider,
    });

    // Eagerly create the custodial wallet (idempotent).
    fetch(`/api/wallet?userId=${encodeURIComponent(u.id)}`).catch(() => {});

    // Seed display username on first login only.
    fetch("/api/user/profile", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ userId: u.id, initUsername: u.name }),
    }).catch(() => {});

    // Issue a server-side session token so mutating API routes can verify
    // the caller is authenticated. Token stored in localStorage.
    fetch("/api/auth/session", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ userId: u.id }),
    })
      .then(r => r.json())
      .then(d => { if (d.token) localStorage.setItem(SESSION_KEY, d.token); })
      .catch(() => {});
  }, []);

  const signOut = useCallback(() => {
    const token = localStorage.getItem(SESSION_KEY);
    if (token) {
      fetch("/api/auth/session", {
        method:  "DELETE",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ token }),
      }).catch(() => {});
    }
    setUser(null);
    localStorage.removeItem(KEY);
    localStorage.removeItem("blackdoor_avatar_v1");
    localStorage.removeItem("blackdoor_username_v1");
    localStorage.removeItem(SESSION_KEY);
  }, []);

  return (
    <Ctx.Provider value={{ user, loading, signIn, signOut }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be inside <AuthProvider>");
  return ctx;
}

// Helper: read the session token from localStorage (call from client components).
export function getSessionToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(SESSION_KEY);
}
